import { Container, Sprite } from 'pixi.js';
import { coinTexture } from './titleArt';
import { prefersReducedMotion } from './ui';

interface Coin {
  sprite: Sprite;
  vx: number;
  vy: number;
  spin: number;
  phase: number;
  size: number;
}

/**
 * A burst of gold coins: they leap up and out from a point, turning over as
 * they go, then fall and fade. Like {@link Confetti}, add one where it should
 * play and call {@link burst}; it cleans up after itself.
 */
export class CoinBurst extends Container {
  private coins: Coin[] = [];
  private running = false;

  constructor() {
    super();
    this.eventMode = 'none';
  }

  clear(): void {
    this.coins.forEach((c) => c.sprite.destroy());
    this.coins = [];
  }

  /** Burst `count` coins from `(x, y)`, about `spread` pixels across, each
   *  about `size` pixels wide. Resolves when the last coin has gone. */
  burst(x: number, y: number, options: { count?: number; spread?: number; size?: number } = {}): Promise<void> {
    if (prefersReducedMotion()) return Promise.resolve();
    const count = options.count ?? 32;
    const spread = options.spread ?? 300;
    const size = options.size ?? 30;
    for (let i = 0; i < count; i++) {
      const sprite = new Sprite(coinTexture());
      sprite.anchor.set(0.5);
      const scale = (size / 48) * (0.75 + Math.random() * 0.5);
      sprite.scale.set(scale);
      sprite.position.set(x + (Math.random() - 0.5) * spread * 0.25, y + (Math.random() - 0.5) * 12);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;
      const speed = (spread / 300) * (7 + Math.random() * 8);
      this.coins.push({
        sprite,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        spin: 0.12 + Math.random() * 0.2,
        phase: Math.random() * Math.PI * 2,
        size: scale,
      });
      this.addChild(sprite);
    }
    return new Promise((resolve) => {
      if (this.running) {
        const wait = () => (this.running ? requestAnimationFrame(wait) : resolve());
        requestAnimationFrame(wait);
        return;
      }
      this.running = true;
      let last = performance.now();
      const frame = (now: number) => {
        if (this.destroyed) {
          this.running = false;
          resolve();
          return;
        }
        const dt = Math.min(3, (now - last) / 16.7);
        last = now;
        for (const c of this.coins) {
          c.vy += 0.42 * dt;
          c.vx *= Math.pow(0.99, dt);
          c.phase += c.spin * dt;
          c.sprite.x += c.vx * dt;
          c.sprite.y += c.vy * dt;
          // A coin turning over: it narrows to its edge and back.
          c.sprite.scale.x = c.size * Math.max(0.12, Math.abs(Math.cos(c.phase)));
          if (c.vy > 3) c.sprite.alpha = Math.max(0, c.sprite.alpha - 0.02 * dt);
        }
        const gone = this.coins.filter((c) => c.sprite.alpha <= 0);
        gone.forEach((c) => c.sprite.destroy());
        this.coins = this.coins.filter((c) => c.sprite.alpha > 0);
        if (this.coins.length > 0) requestAnimationFrame(frame);
        else {
          this.running = false;
          resolve();
        }
      };
      requestAnimationFrame(frame);
    });
  }
}
